import React from 'react';
import { Descriptions, Empty, Table, Tag, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { PotParticipant, PotRound, PotState } from './types';
import { isPotAvailable } from '../../config/env';

const number = (value?: number | null) => value == null ? '-' : value.toLocaleString();
export const potPlayer = (player?: PotParticipant | null) => !player ? '-' : player.is_bot
  ? <Tag color="purple">Bot {player.bot_instance_id || player.user_id}</Tag>
  : <Link to={`/users/${player.user_id}`}>User #{player.user_id}</Link>;

export function PotSummary({ pot }: { pot?: PotRound | null }) {
  if (!isPotAvailable()) return <Typography.Text type="secondary">Coming soon</Typography.Text>;
  if (!pot) return <>-</>;
  const transition = pot.end || pot.start;
  const claim = pot.end?.claim;
  return <div><Tag>{pot.status}</Tag><span>Cycle {number(transition?.cycle)} · {number(transition?.cups)} cups</span>
    {claim && <div>{potPlayer(claim.winner)} · {number(claim.amount)} {claim.virtual ? 'virtual gold' : 'gold reward'}</div>}
  </div>;
}

export function PotDetails({ pot }: { pot?: PotRound | null }) {
  if (!isPotAvailable()) return <Typography.Text type="secondary">Coming soon</Typography.Text>;
  if (!pot) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Pot snapshot recorded for this round" />;
  const transition = pot.end || pot.start;
  const claim = pot.end?.claim;
  return <div className="mt-4">
    <Descriptions title="Pot details" bordered size="small" column={{ xs: 1, sm: 2 }} items={[
      { key: 'round', label: 'Server round ID', children: pot.round_id },
      { key: 'status', label: 'Pot round status', children: <Tag>{pot.status}</Tag> },
      { key: 'enabled', label: 'Enabled', children: transition ? (transition.enabled ? 'Yes' : 'No') : '-' },
      { key: 'cycle', label: 'Cycle', children: number(transition?.cycle) },
      { key: 'rounds', label: 'Rounds in cycle', children: number(transition?.n) },
      { key: 'cups', label: 'Cups / required', children: transition ? `${transition.cups} / ${transition.cups_to_claim}` : '-' },
      { key: 'holder', label: 'Cup holder', children: potPlayer(transition?.cup_holder) },
      { key: 'previous', label: 'Previous cups', children: number(transition?.previous_cups) },
      { key: 'claim-cycle', label: 'Claimed cycle', children: number(claim?.cycle) },
      { key: 'winner', label: 'Claim winner', children: potPlayer(claim?.winner) },
      { key: 'gross', label: 'Gross reward', children: number(claim?.gross_amount) },
      { key: 'rake', label: 'Pot rake', children: number(claim?.rake_amount) },
      { key: 'net', label: 'Net reward', children: claim ? `${number(claim.amount)}${claim.virtual ? ' (virtual bot reward)' : ''}` : '-' },
    ]} />
    <Typography.Title level={5}>Pot contributions and changes</Typography.Title>
    <Table size="small" pagination={false} rowKey={participant => participant.is_bot ? `bot:${participant.bot_instance_id || participant.user_id}` : `user:${participant.user_id}`} scroll={{ x: 650 }}
      dataSource={pot.participants || []} columns={[
        { title: 'Player', render: (_, participant) => potPlayer(participant) },
        { title: 'Bet', dataIndex: 'bet', render: number },
        { title: 'Ante', dataIndex: 'ante_amount', render: (value?: number) => number(value ?? 0) },
        { title: 'Pot before', render: (_, participant) => number(transition?.players?.find(p => p.user_id === participant.user_id)?.pot_before) },
        { title: 'Pot after', render: (_, participant) => number(transition?.players?.find(p => p.user_id === participant.user_id)?.pot_after) },
        { title: 'Pot gold change', render: (_, participant) => number(transition?.players?.find(p => p.user_id === participant.user_id)?.pot_gold_delta) },
      ]} />
  </div>;
}

export function CurrentPotDetails({ protocol, state }: { protocol?: string; state?: PotState }) {
  if (!isPotAvailable()) return <Typography.Text type="secondary">Coming soon</Typography.Text>;
  return <Descriptions title="Current Pot" size="small" column={1} items={[
    { key: 'protocol', label: 'Protocol', children: protocol || '-' },
    { key: 'cycle', label: 'Cycle', children: number(state?.cycle) },
    { key: 'rounds', label: 'Rounds in cycle', children: number(state?.n) },
    { key: 'cups', label: 'Cups / required', children: state?.cups == null ? '-' : `${state.cups} / ${state.config?.cups_to_claim ?? '-'}` },
    { key: 'holder', label: 'Holder membership', children: state?.holder || '-' },
    { key: 'cap', label: 'Pot cap', children: number(state?.config?.cap) },
    { key: 'rake', label: 'Rake %', children: state?.config?.rake_percent ?? '-' },
    { key: 'participants', label: 'Contributions', children: state?.participants?.length
      ? state.participants.map((participant, index) => <div key={index}>{potPlayer(participant)} · ante {number(participant.ante_amount)}{participant.departed ? ' (departed)' : ''}</div>) : '-' },
  ]} />;
}
